// src/lib/memberId.js
import { doc, runTransaction, serverTimestamp } from "firebase/firestore";

/**
 * Generates a new memberId in the format YYYY000XXX using a Firestore transaction.
 * - Uses sequences/members with fields: { lastNumber: number, year: number }
 * - Resets the counter when the year changes.
 * Returns the string memberId, e.g. "202500001".
 */
export async function generateMemberId(db) {
  // Validate db parameter
  if (!db) {
    throw new Error('Firestore database instance is required but was not provided');
  }
  
  // Check if db is a valid Firestore instance
  if (!db || typeof db !== 'object') {
    throw new Error('Invalid Firestore database instance provided. Expected Firestore instance.');
  }

  try {
    const yearNow = new Date().getFullYear();
    const seqRef = doc(db, "sequences", "members");

    const nextNumber = await runTransaction(db, async (tx) => {
      const snap = await tx.get(seqRef);

      // CASE 1: First time (doc doesn't exist) -> create with lastNumber = 1
      if (!snap.exists()) {
        tx.set(seqRef, {
          lastNumber: 1,
          year: yearNow,
          updatedAt: serverTimestamp(),
        });
        return 1;
      }

      // CASE 2: Doc exists
      const data = snap.data() || {};
      const lastYear = data.year || yearNow;
      const lastNum = data.lastNumber || 0;

      // If year changed, reset to 1; else increment by 1
      const inc = (lastYear !== yearNow) ? 1 : lastNum + 1;

      tx.update(seqRef, {
        lastNumber: inc,
        year: yearNow,
        updatedAt: serverTimestamp(),
      });

      return inc;
    });

    // Build YYYY000XXX ( ok pad to 3 digits)
    const suffix = String(nextNumber).padStart(3, "0");
    return `${yearNow}000${suffix}`;
  } catch (error) {
    console.error('Error generating member ID:', error);
    throw new Error(`Failed to generate member ID: ${error.message}`);
  }
}
